import React from "react";
import Button from "components/button/Button";
import TextField from "components/fields/TextField";
import InputField from "components/fields/InputField";
import { ICategory } from "views/admin/goods/types/category.interface";
import { addCategory, updateCategory } from "api/CategoryAPI";
import { useDisclosure } from "@chakra-ui/hooks";
import { Actions } from "constants/actions";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@chakra-ui/modal";
import DropzoneFile from "components/dropzone";

interface ICreateUpdateModalProps {
  title: string;
  action: Actions;
  data: ICategory;
  onRefresh?: () => void;
  onCancel?: () => void;
}

const CreateUpdateModal: React.FC<ICreateUpdateModalProps> = (props) => {
  const { title, action, data, onRefresh, onCancel } = props;
  const { onOpen } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const dropFile = (files: File[]) => {
    setFiles([...files]);
  };

  const removeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const arrangeFiles = (files: File[]) => {
    setFiles([...files]);
  };

  const changeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const onSubmit = (data: any): void => {
    setIsLoading(true);
    let promise = null;

    if (files.length > 0) data.image = files[0];

    if (action === Actions.CREATE) promise = addCategory(data);
    else if (action === Actions.UPDATE)
      promise = updateCategory(data.slug, data);

    toast.promise(promise, {
      loading: "Menyimpan kategori...",
      success: (res) => {
        onCancel();
        onRefresh();
        reset({});
        setIsLoading(false);
        return res.data.message;
      },
      error: (err) => {
        setIsLoading(false);
        return err?.response?.data?.message || "Gagal menyimpan kategori!";
      },
    });
  };

  React.useEffect(() => {
    if (action === Actions.CREATE) {
      reset({});
      onOpen();
    } else if (action === Actions.UPDATE) {
      reset(data);
      onOpen();
    }
  }, [action, data, onOpen, reset]);

  return (
    <form encType="multipart/form-data" onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>{title}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <section
          className={`mb-3 grid h-fit ${
            data?.image ? "grid-cols-2" : "grid-cols-1"
          } gap-3`}
        >
          {data?.image ? (
            <section className="h-fit">
              <label className="label">Foto / Icon saat ini</label>
              <div className="mb-3 h-full w-full rounded-xl bg-white bg-clip-border p-3 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
                <img
                  src={`${process.env.REACT_APP_PUBLIC_URL}/${data?.image}`}
                  alt={data?.name}
                  className="h-full w-full rounded-xl object-cover object-center"
                />
              </div>
            </section>
          ) : null}
          <section className="h-fit">
            <label className="label">Foto / Icon baru</label>
            <DropzoneFile
              text="Foto / Icon Kategori"
              accept={{
                "image/*": [".jpg", ".png", ".jpeg"],
              }}
              maxSize={2048000}
              maxFiles={1}
              files={files}
              dropFile={(files: File[]) => dropFile(files)}
              removeFile={(files: File[]) => removeFile(files)}
              arrangeFiles={(files: File[]) => arrangeFiles(files)}
              changeFile={(files: File[]) => changeFile(files)}
            />
          </section>
        </section>
        <InputField
          extra="mb-3"
          label="Nama Kategori"
          placeholder="Masukkan nama kategori"
          id="name"
          type="text"
          variant="outline"
          register={register}
          errors={errors}
          validationSchema={{
            required: "Kategori harus diisi!",
          }}
        />
        <TextField
          extra="mb-3 flex-1"
          label="Deskripsi"
          placeholder="Deskripsi kategori"
          id="description"
          variant="outline"
          register={register}
          errors={errors}
          validationSchema={{}}
        />
      </ModalBody>
      <ModalFooter>
        <div className="flex gap-2">
          <Button
            variant="primary"
            children="Simpan"
            size="fit"
            type="submit"
            isLoading={isLoading}
          />
          <Button
            variant="gray"
            children="Batal"
            size="fit"
            type="button"
            onClick={onCancel}
          />
        </div>
      </ModalFooter>
    </form>
  );
};

export default CreateUpdateModal;
